import { Injectable } from '@angular/core';
import { DynamicConfig } from '@statsig/js-client';

import { StatsigService } from '@services/statsig.service';
import { Session } from '@services/session';

// define an enum if feature gate key is used anywhere else in the app
export enum COMPLIANCE_FEATURE_FLAGS {
  COMPLIANCE_NON_DOT_MOVEMENT_V2_ENABLED = 'compliance-non-dot-movement-v2-enabled',
  FEDEX_LOGS_CHANGES_ENABLED = 'fedex_logs_changes_enabled',
  COMPLIANCE_CSA_ACTIVITY_CRASHES_ENABLED = 'compliance-csa-activity-crashes-enabled',
  COMPLIANCE_CSA_ACTIVITY_ENABLED = 'compliance-csa-activity-enabled',
  COMPLIANCE_NON_DOT_MOVEMENT_ENABLED = 'compliance-non-dot-movement-enabled',
  COMPLIANCE_TEAM_DRIVING_ENABLED = 'compliance-team-driving-enabled',
  COMPLIANCE_SMART_TRIP_MATCHING_ENABLED = 'compliance-smart-trip-matching-enabled',
  COMPLIANCE_CYCLE_SWITCHING_ENABLED = 'compliance-cycle-switching-enabled',
  COMPLIANCE_FIX_DISTANCE_CALCULATIONS_ENABLED = 'compliance-fix-distance-calculations-enabled',
  COMPLIANCE_HUB_CORRECT_PERCENTAGE_CHANGE_ENABLED = 'compliance-hub-correct-percentage-change-enabled',
  ETT_HARDWIRED_MODE_REPORT_AND_ALERT = 'ett-hardwired-mode-report-and-alert',
  COMPLIANCE_TRIPS_NEW_AI_LABELS_PHASE_2_ENABLED = 'compliance-trips-new-ai-labels-phase-2-enabled',
  COMPLIANCE_AG_EXCEPTION_ENABLED = 'compliance-ag-exception-enabled',
  COMPLIANCE_AG_EXCEPTION_V2_ENABLED = 'compliance-ag-exception-v2-enabled',
  COMPLIANCE_DRIVER_QUALIFICATION_ENABLED = 'compliance-driver-qualification-enabled',
  COMPLIANCE_VEHICLE_QUALIFICATION_ENABLED = 'compliance-vehicle-documents-enabled',
  COMPLIANCE_LEADERBOARD_PRECOMPUTE_ENABLED = 'compliance-leaderboard-precompute-enabled',
  COMPLIANCE_OVERVIEW_DAILY_FILTER_ENABLED = 'compliance-overview-daily-filter-enabled',
  COMPLIANCE_BULK_UNASSIGN_TRIPS_ENABLED = 'compliance-bulk-unassign-trips-enabled',
  COMPLIANCE_VEHICLE_DOCUMENTS_ALERTS_AND_REPORTS_ENABLED = 'compliance-vehicle-documents-alerts-and-reports-enabled',
  COMPLIANCE_YM_ENHANCEMENTS_ENABLED = 'compliance-ym-enhancements-enabled',
  COMPLIANCE_DECOUPLING_OF_TRIPS_ENABLED = 'compliance-decoupling-of-trips-enabled',
  COMPLIANCE_UNREGULATED_FACE_MATCH_ENABLED = 'compliance-unregulated-face-match-enabled',
  COMPLIANCE_TEXAS_OIL_AND_GAS_CYCLE_ENABLED = 'compliance-texas-oil-and-gas-cycle-enabled',
  COMPLIANCE_NUMBER_FROZEN_DISABLED = 'compliance-number-frozen-disabled',
  COMPLIANCE_CSA_INSIGHTS_SKIP_CANADIAN_CYCLE_CHECK_ENABLED = 'compliance-csa-insights-skip-canadian-cycle-check-enabled',
  COMPLIANCE_LOGS_TAB_DEFAULT_TODAY_ENABLED = 'compliance-logs-tab-default-today-enabled',
  COMPLIANCE_LOG_DETAIL_QUICK_LINKS_ENABLED = 'compliance-log-detail-quick-links-enabled',
  COMPLIANCE_ALLOW_EDIT_BEFORE_DRIVER_CERTIFIES_LOG_ENABLED = 'compliance-allow-edit-before-driver-certifies-log-enabled',
  COMPLIANCE_PERFORMANCE_ISSUES = 'compliance-performance-issues',
  COMPLIANCE_LOG_EDIT_PHX_MIGRATION_ENABLED = 'compliance-log-edit-phx-migration-enabled',
  COMPLIANCE_LOG_DETAILS_PAGE_RESKIN_ENABLED = 'compliance-log-details-page-reskin-enabled',
  COMPLIANCE_DATE_RANGE_LIMITS_CONFIG = 'compliance-date-range-limits-config',
  COMPLIANCE_DRIVER_ID_ORIGIN_FILTERS_ENABLED = 'compliance-driver-id-origin-filters-enabled',
  COMPLIANCE_ASSIGNABLE_API_REPLACEMENT_ENABLED = 'compliance-assignable-api-replacement-enabled',
  COMPLIANCE_REPLACE_NEGATIVE_DISTANCE = 'compliance-replace-negative-trip-distance',
  COMPLIANCE_TRIPS_DETAIL_REPORT_FE_PAGINATION = 'compliance-trips-detail-report-fe-pagination',
  COMPLIANCE_DEFAULT_DATE_RANGE_CONFIG = 'compliance-default-date-range-config',
  COMPLIANCE_DECOUPLING_OF_TRIPS_PHASE_2_ENABLED = 'compliance-decoupling-of-trips-phase-2-enabled',
  COMPLIANCE_LOGS_WITHOUT_DVIR_FE_PAGINATION = 'compliance-logs-without-dvir-fe-pagination',
  COMPLIANCE_LOGS_DETAIL_FE_PAGINATION = 'compliance-logs-detail-fe-pagination',
  COMPLIANCE_LOGS_DETAIL_REPORT_DRIVER_SELECTION_REQUIRED = 'compliance-logs-detail-report-driver-selection-required',
  DI_REAL_TIME_FACE_MATCH_SAFETY_INTEGRATION_ENABLED = 'di-rtfm-safety-integration-enabled',
  DI_REAL_TIME_FACE_MATCH_TRIPS_INTEGRATION_ENABLED = 'di-rtfm-trips-integration-enabled',
  DI_REAL_TIME_FACE_MATCH_FLEETVIEW_INTEGRATION_ENABLED = 'di-rtfm-fleetview-integration-enabled',
  COMPLIANCE_HIDE_ANNOTATED_UDTS_ENABLED = 'compliance-hide-annotated-udts-enabled',
}

@Injectable({ providedIn: 'root' })
export class ComplianceFeatureFlagService {
  private complianceDateRangeLimitsConfig: DynamicConfig;
  private complianceDefaultDateRangeConfig: DynamicConfig;

  constructor(private statsigService: StatsigService, private session: Session) {
    this.complianceDateRangeLimitsConfig = this.statsigService.evaluateDynamicConfig(
      COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_DATE_RANGE_LIMITS_CONFIG,
    );
    this.complianceDefaultDateRangeConfig = this.statsigService.evaluateDynamicConfig(
      COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_DEFAULT_DATE_RANGE_CONFIG,
    );
  }

  public isNonDotMovementV2Enabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_NON_DOT_MOVEMENT_V2_ENABLED);
  }

  public isDrivingPeriodsPartitioningEnabled(): boolean {
    return true;
  }

  public isFedexVersionOfLogsEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.FEDEX_LOGS_CHANGES_ENABLED);
  }

  public isComplianceNonDotMovementEnabled(): boolean {
    return (
      this.isNonDotMovementV2Enabled() ||
      this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_NON_DOT_MOVEMENT_ENABLED)
    );
  }

  public isTeamDrivingEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_TEAM_DRIVING_ENABLED);
  }

  public isComplianceCSAActivityEnabled(): boolean {
    return (
      this.session.isComplianceCSAActivityAllowed() &&
      this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_CSA_ACTIVITY_ENABLED)
    );
  }

  public isComplianceCSAActivityCrashesEnabled(): boolean {
    return (
      this.isComplianceCSAActivityEnabled() &&
      this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_CSA_ACTIVITY_CRASHES_ENABLED)
    );
  }

  public isComplianceAllowEditBeforeDriverCertifiesLogEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_ALLOW_EDIT_BEFORE_DRIVER_CERTIFIES_LOG_ENABLED);
  }

  public isComplianceHideAnnotatedUDTsEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_HIDE_ANNOTATED_UDTS_ENABLED);
  }

  public isSTMEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_SMART_TRIP_MATCHING_ENABLED);
  }

  public isCycleSwitchingEnabledforCompliance(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_CYCLE_SWITCHING_ENABLED);
  }

  public isComplianceTripsNewAILabelsPhaseTwoEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_TRIPS_NEW_AI_LABELS_PHASE_2_ENABLED);
  }

  public isComplianceFixDistanceCalculationsEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_FIX_DISTANCE_CALCULATIONS_ENABLED);
  }

  public isComplianceHubCorrectPercentageChangeEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_HUB_CORRECT_PERCENTAGE_CHANGE_ENABLED);
  }

  public isETTHardwiredModeReportAndAlertEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.ETT_HARDWIRED_MODE_REPORT_AND_ALERT);
  }

  public isComplianceAgExceptionEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_AG_EXCEPTION_ENABLED);
  }

  public isComplianceAgExceptionV2Enabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_AG_EXCEPTION_V2_ENABLED);
  }

  public isComplianceLeaderboardPrecomputeEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LEADERBOARD_PRECOMPUTE_ENABLED);
  }

  public isComplianceOverviewDailyFilterEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_OVERVIEW_DAILY_FILTER_ENABLED);
  }

  public isComplianceYMEnhancementsEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_YM_ENHANCEMENTS_ENABLED);
  }

  public isComplianceDecouplingOfTripsEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_DECOUPLING_OF_TRIPS_ENABLED) ||
     this.isComplianceDecouplingOfTripsPhase2Enabled();
  }

  public isComplianceDecouplingOfTripsPhase2Enabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_DECOUPLING_OF_TRIPS_PHASE_2_ENABLED);
  }

  public isComplianceUnregulatedFaceMatchEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_UNREGULATED_FACE_MATCH_ENABLED);
  }

  public isComplianceTexasOilAndGasCycleEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_TEXAS_OIL_AND_GAS_CYCLE_ENABLED);
  }

  public isComplianceNumberFrozenDisabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_NUMBER_FROZEN_DISABLED);
  }

  public isComplianceCsaInsightsSkipCanadianCycleCheckEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_CSA_INSIGHTS_SKIP_CANADIAN_CYCLE_CHECK_ENABLED);
  }

  public isComplianceLogsTabDefaultTodayEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LOGS_TAB_DEFAULT_TODAY_ENABLED);
  }

  public isComplianceLogDetailQuickLinksEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LOG_DETAIL_QUICK_LINKS_ENABLED);
  }

  public isTripListFEPaginationEnabled(): boolean {
    return this.statsigService.evaluateDynamicConfig(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_PERFORMANCE_ISSUES).value[
      'CPLAT-1478'
    ] as boolean;
  }

  public isComplianceLogEditPhxMigrationEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LOG_EDIT_PHX_MIGRATION_ENABLED);
  }

  public isComplianceLogDetailsPageReskinEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LOG_DETAILS_PAGE_RESKIN_ENABLED);
  }

  public isLogsDetailNavigationApiSegregated(): boolean {
    return this.statsigService.evaluateDynamicConfig(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_PERFORMANCE_ISSUES).value[
      'CPLAT-1553'
    ] as boolean;
  }

  public logsListDateRangeLimit(): number {
    return this.complianceDateRangeLimitsConfig.value['logs-list'] as number;
  }

  public isComplianceDriverIdOriginFiltersEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_DRIVER_ID_ORIGIN_FILTERS_ENABLED);
  }

  public isComplianceAssignableApiReplacementEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_ASSIGNABLE_API_REPLACEMENT_ENABLED);
  }

  public isComplianceReplaceNegativeDistanceEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_REPLACE_NEGATIVE_DISTANCE);
  }

  public isComplianceTripsDetailReportFEPaginationEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_TRIPS_DETAIL_REPORT_FE_PAGINATION);
  }

  public logsWithoutInspectionReportDefaultDateRange(): number {
    return this.complianceDefaultDateRangeConfig.value['logs-without-inspection-report'] as number;
  }

  public isComplianceLogsWithoutDvirFEPaginationEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LOGS_WITHOUT_DVIR_FE_PAGINATION);
  }

  public isComplianceLogsDetailFEPaginationEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LOGS_DETAIL_FE_PAGINATION);
  }

  public isComplianceLogsDetailReportDriverSelectionRequired(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.COMPLIANCE_LOGS_DETAIL_REPORT_DRIVER_SELECTION_REQUIRED);
  }

  public isSafetyRealTimeFaceMatchEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.DI_REAL_TIME_FACE_MATCH_SAFETY_INTEGRATION_ENABLED);
  }

  public isDriverIdRealTimeFaceMatchFleetviewIntegrationEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.DI_REAL_TIME_FACE_MATCH_FLEETVIEW_INTEGRATION_ENABLED);
  }
  
  public isDriverIdRealTimeFaceMatchTripsIntegrationEnabled(): boolean {
    return this.statsigService.evaluateGate(COMPLIANCE_FEATURE_FLAGS.DI_REAL_TIME_FACE_MATCH_TRIPS_INTEGRATION_ENABLED);
  };
}
